// ================================================================================
// The TB-elimination pledge text in three languages (English / हिंदी / मराठी).
// Source text is supplied verbatim by the product owner — keep it byte-for-byte.
// This file is UTF-8; the Devanagari (Hindi + Marathi) glyphs are stored as
// native characters (NOT unicode-escaped) so they survive the build intact.
// The OathPledge component applies a Devanagari-capable font stack so the
// Hindi/Marathi text renders cleanly with no broken glyphs.
// ================================================================================

export type OathLang = 'en' | 'hi' | 'mr'

export interface OathContent {
  /** BCP-47 lang code — set on the text container so browsers pick the right font. */
  lang: 'en' | 'hi' | 'mr'
  /** Big pledge heading. */
  title: string
  /** Optional strapline shown under the title (the line after the title). */
  subtitle?: string
  /** Body paragraphs, rendered in order. */
  paragraphs: string[]
  /** The accept-checkbox label (rendered next to a real <input type=checkbox>). */
  checkboxLabel: string
}

export const OATH_TEXT: Record<OathLang, OathContent> = {
  en: {
    lang: 'en',
    title: 'Take the Pledge. Make India TB-Free!',
    paragraphs: [
      'I pledge to do my part in making India TB-free.',
      'I will learn about TB, spread awareness, encourage timely testing and treatment, and support people affected by TB with care and dignity.',
      'I will not allow stigma or misinformation to come in the way of someone seeking help.',
      'Together, through awareness, compassion and action, let us build a TB Mukt Bharat.',
    ],
    checkboxLabel: 'I take this pledge.',
  },
  hi: {
    lang: 'hi',
    title: 'एक संकल्प, टीबी-मुक्त भारत',
    paragraphs: [
      'मैं भारत को टीबी-मुक्त बनाने में अपना योगदान देने का संकल्प लेता/लेती हूँ।',
      'टीबी के बारे में सही जानकारी हासिल करना और अपने परिवार, दोस्तों और समाज में जागरूकता फैलाना मेरा संकल्प है। समय पर टीबी की जाँच और इलाज के लिए प्रोत्साहित करना और टीबी से प्रभावित लोगों को सम्मान व सहयोग देना भी मेरी जिम्मेदारी है।',
      'मैं टीबी से जुड़े भेदभाव, कलंक और गलत जानकारी का विरोध करूँगा/करूँगी और जरूरत के समय सही मदद तक पहुँचने में सहयोग दूँगा/दूँगी।',
      'आइए, मिलकर जागरूक बनें, साथ दें और भारत को टीबी-मुक्त बनाएं।',
    ],
    checkboxLabel: 'मैं यह संकल्प स्वीकार करता/करती हूँ।',
  },
  mr: {
    lang: 'mr',
    title: 'माझी प्रतिज्ञा, टीबीमुक्त भारताची दिशा',
    paragraphs: [
      'टीबीमुक्त भारतासाठी माझाही हातभार लागेल, अशी मी आज प्रतिज्ञा घेतो/घेते.',
      'टीबीबद्दल योग्य माहिती जाणून घेईन आणि ती इतरांपर्यंत पोहोचवीन. वेळेवर तपासणी आणि उपचार घेण्यासाठी स्वतः पुढाकार घेईन आणि इतरांनाही प्रोत्साहन देईन.',
      'टीबी झालेल्या व्यक्तीकडे तिरस्काराने किंवा भेदभावाने न पाहता, त्यांना समजून घेईन आणि साथ देईन. टीबीबद्दलचे गैरसमज आणि चुकीची माहिती दूर करण्यासाठीही प्रयत्न करीन.',
      'आपली जागरूकता, आपली साथ आणि आपली कृती—यातूनच घडेल टीबीमुक्त भारत!',
    ],
    checkboxLabel: 'मी ही प्रतिज्ञा घेतो/घेते.',
  },
}

/** Language picker labels (shown in their own script). */
export const OATH_LANG_LABELS: { code: OathLang; label: string }[] = [
  { code: 'en', label: 'English' },
  { code: 'hi', label: 'हिंदी' },
  { code: 'mr', label: 'मराठी' },
]